import { useLocation, useRoute } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, Mail, Building2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useInviteAcceptance } from "@/hooks/useInviteAcceptance";
import { InviteSignupForm } from "@/components/InviteSignupForm";

export default function AcceptInvite() {
  const [, params] = useRoute("/accept-invitation/:token");
  const [, setLocation] = useLocation();
  const { isLoading: isAuthLoading } = useAuth();
  
  const {
    status,
    errorMessage,
    inviteDetails,
    handleSignupAndAccept,
  } = useInviteAcceptance({
    token: params?.token,
    onSuccess: () => {
      // Redirect to workspace after 2 seconds
      setTimeout(() => {
        setLocation("/");
      }, 2000);
    },
    onError: (message) => {
      console.error("[AcceptInvite] Error:", message);
    },
  });

  // Loading state - checking auth
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading...
            </CardTitle>
            <CardDescription>
              Checking your authentication status...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Success state
  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Invitation Accepted!
            </CardTitle>
            <CardDescription>
              Welcome to {inviteDetails?.companyName || "the company"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              You have successfully joined the company. Redirecting you to your workspace...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Invitation Error
            </CardTitle>
            <CardDescription>
              There was a problem accepting your invitation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <Mail className="h-5 w-5 text-red-600 mt-0.5" />
                <p className="text-sm text-red-800">
                  {errorMessage}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setLocation("/login")}
                  className="flex-1"
                >
                  Back to Login
                </Button>
                <Button
                  onClick={() => setLocation("/")}
                  className="flex-1"
                >
                  Go to Workspace
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not authenticated - show signup form (invitations are only for new users)
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Join Company via Invitation
          </CardTitle>
          <CardDescription>
            Create your account to accept the invitation and join {inviteDetails?.companyName || "the company"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <InviteSignupForm
            onSubmit={handleSignupAndAccept}
            isLoading={status === "loading"}
            defaultEmail={inviteDetails?.email}
          />
        </CardContent>
      </Card>
    </div>
  );
}
